/**
 * DragAndDropService — Handles all drag-and-drop interactions in the game.
 *
 * Supports both desktop (HTML5 Drag API) and mobile (Touch Events).
 *
 * Responsibilities:
 * - Drag cards from hand to battlefield (play card)
 * - Drag minions on battlefield to reposition (insert at new position)
 * - Drag cards from hand OR battlefield into the sell zone (the area above
 *   the battlefield / play area) to sell them
 * - Visual feedback: ghost element, drop zone highlight, placeholder,
 *   sell-zone highlight (top-half overlay)
 * - Phase-aware: only allow drag operations during preparation phase
 *
 * This service is purely frontend — it reads DOM state and fires callbacks.
 * The backend is authoritative; the service sends intentions and waits for
 * the server to produce the resulting state.
 */

import { COOP_GIVE_COST } from '../../shared/models/CoopMissions';
import { getSpellDefinition, SPELL_MINION_TYPE } from '../../shared/models/SpellDefinitions';
import { GameConfig } from '../../config/gameConfig';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DragSource = 'hand' | 'battlefield';

export interface DragData {
  source: DragSource;
  instanceId: string;
  originalIndex: number;
  /**
   * Card identity for the drag (hand cards): the base definition ID and the
   * class. Used to route SPELL drags — a hand card that is a spell casts on
   * board release (onPlaySpell) instead of playing (onPlayCard): targeted
   * spells target the minion under the release point, battlefield spells
   * cast from anywhere over the board.
   */
  baseMinionId?: string;
  minionType?: string;
}

export type DragStartCallback = (dragData: DragData, element: HTMLElement) => void;
export type DragEndCallback = (dragData: DragData, succeeded: boolean) => void;
export type PlayCardCallback = (instanceId: string, targetPosition?: number) => void;
export type RepositionCallback = (instanceId: string, targetIndex: number) => void;
export type SellCardCallback = (instanceId: string) => void;

export interface DragAndDropOptions {
  isDragEnabled: boolean;
  onPlayCard: PlayCardCallback;
  onReposition: RepositionCallback;
  /**
   * Invoked when a card (from hand OR battlefield) is dropped on the sell
   * drop zone — the area above the battlefield (play area). The backend is
   * authoritative: the caller sends a SellMinionRequest and the server
   * validates the sale
   * (preparation phase, minion ownership) and produces the resulting state.
   */
  onSellCard?: SellCardCallback;
  /**
   * Invoked when a card is dropped on the GIVE half of the (split) sell
   * zone — COOP team matches only (see giveZoneEnabled): the caller sends
   * a GiveMinionRequest (the server validates the give — 5 gold, prep
   * phase, teammate hand capacity).
   */
  onGiveCard?: SellCardCallback;
  /**
   * COOP team match: the sell zone SPLITS vertically in two equal parts —
   * LEFT half = sell (gold = level), RIGHT half = give to teammate (5
   * gold). Off (the default) the whole zone sells, as in regular matches.
   */
  giveZoneEnabled?: boolean;
  /**
   * The current player's board capacity for the drop affordances (the red
   * "No space" indicator when the board is full, task 175). Defaults to
   * GameConfig.maxBoardSize (7); main.ts sets 5 for COOP team matches
   * (COOP_HUMAN_BOARD_SIZE) via updateOptions.
   */
  boardSizeLimit?: number;
  /**
   * Invoked when a SPELL card from the hand is released over the
   * battlefield: the spell CASTS (the effect goes into play and the card
   * despawns — it never lands on the board). Targeted spells ('minion'
   * target in the shared SpellDefinitions spec) are only cast when the
   * release lands on a specific battlefield minion — then `targetInstanceId`
   * is that minion; released off-target they do nothing (the card stays in
   * hand). 'battlefield' spells cast with no target from anywhere over the
   * board. The backend is authoritative: the caller sends a
   * PlaySpellRequest and the server validates the cast.
   *
   * `releasePos` is the VIEWPORT point where the card was released (the
   * drag drop point, or the cast card's center for button/double-click
   * casts) — the caller plays the star-explosion cast FX there.
   */
  onPlaySpell?: (instanceId: string, targetInstanceId?: string, releasePos?: { x: number; y: number }) => void;
  onDragStart?: DragStartCallback;
  onDragEnd?: DragEndCallback;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DRAGGING_CLASS = 'dragging';
const DROP_TARGET_CLASS = 'drop-target';
const PLACEHOLDER_CLASS = 'drag-placeholder';
/**
 * SPELL TARGET GLOW: the gold glow on a battlefield minion while a
 * minion-target spell is being dragged over it (the drop is valid — the
 * cast will target that minion). Set on the minion's dragover (desktop) and
 * by the touch-move hit test (mobile), cleared on dragleave / dragend /
 * touch-end. Purely visual (CSS .spell-target-glow).
 */
const SPELL_TARGET_GLOW_CLASS = 'spell-target-glow';
const DRAG_DATA_KEY = 'project-x-drag-data';
const SELL_ZONE_CLASS = 'sell-drop-zone';
const SELL_ZONE_ACTIVE_CLASS = 'sell-drop-zone-active';
/** Base highlight: the sell zone is ALWAYS visible while a card drag is live (the user must see the sell boundary); .active strengthens it when the pointer/finger is inside. */
const SELL_ZONE_VISIBLE_CLASS = 'visible';
/**
 * Event marker set by the SPECIFIC drop zones (battlefield play, minion/
 * placeholder reposition) so the document-level sell-zone handler knows the
 * drop was already claimed. An explicit marker (instead of relying on
 * e.defaultPrevented) keeps each document-level listener independent —
 * defaultPrevented would also be set by another sell-zone handler itself.
 */
export const DROP_HANDLED_MARKER = 'project-x-drop-handled';
/**
 * px — minimum movement to count as a touch drag (vs a hold/long-press).
 * Exported because CardInspectService's touch movement tolerance MUST be
 * this exact value: a finger that merely JITTERS during a deliberate
 * "hold to read" press (the 600 ms long-press preview) stays under it, so
 * long-pressing never picks the card up — while a real drag crosses it
 * within a few milliseconds (a finger moves at least ~100 px/s, so 10 px
 * is ~100 ms at a leisurely pace) and stays responsive. If the two
 * services ever diverge, the preview either flashes over an active drag
 * (inspect tolerance > drag threshold) or dies from harmless jitter
 * (inspect tolerance < drag threshold).
 */
export const TOUCH_DRAG_THRESHOLD = 10;

/**
 * Mouse drag threshold (px) before a mouse press becomes a drag. Tighter
 * than the touch threshold — the mouse has no tap-vs-scroll ambiguity, so
 * the drag starts almost immediately (matching the native ~4-5 px feel).
 */
export const MOUSE_DRAG_THRESHOLD = 5;
/**
 * ms — a touch press that stays still this long is a LONG-PRESS (the
 * CardInspectService preview), not a drag. Exported because CardInspectService
 * uses this exact duration for its touch preview timer, AND this service uses
 * it for the long-press DRAG LOCK (below): once a press has been still for
 * this long it can never start a drag. If the two ever diverged, a completed
 * long-press could still turn into a drag (the "long-press sells my card" bug).
 *
 * 600 ms (user-requested, was 2000): short enough that a deliberate
 * "hold to read" feels instant, long enough that a drag (which starts moving
 * within a few tens of ms) never crosses it. It sits just after the OS
 * long-press recognition (~500 ms) — harmless: on touch-first devices there
 * is no contextmenu listener (no OS reaction), and the drag lock means the
 * press can no longer turn into a drag at this point.
 */
export const LONG_PRESS_TOUCH_MS = 600;
/**
 * px — the sell zone's lower boundary sits this far ABOVE the top of the
 * played cards (the user's "about 10px above the edge of the played cards"): the
 * sell zone no longer overlaps the card tops, and the small band just above
 * the cards belongs to the board side, so a release hovering over the card
 * row can never accidentally sell. The boundary is derived from the LIVE
 * card geometry, so it holds on every screen size (empty board → the
 * container top is the fallback anchor).
 */
const SELL_ZONE_CARD_OFFSET_PX = 10;

// ─── DragAndDropService ─────────────────────────────────────────────────────

export class DragAndDropService {
  private _options: DragAndDropOptions;
  private _currentDrag: DragData | null = null;
  private _dragSourceElement: HTMLElement | null = null;
  private _placeholder: HTMLElement | null = null;
  private _handDropPlaceholder: HTMLElement | null = null;
  private _spellZoneEl: HTMLElement | null = null;

  // Sell drop zone (area above the battlefield / play area)
  private _sellZone: HTMLElement | null = null;
  private _globalSellZoneInstalled = false;

  /** Document-level dragover for the sell drop zone (bound for dispose()). */
  private _onGlobalDragOver = (e: DragEvent): void => {
    const dt = e.dataTransfer as DataTransfer | null;
    if (!dt) return;
    // Only react to Project_X card drags (file drags etc. must keep their
    // default behavior). During dragover the data itself is protected, but
    // the custom type IS visible in dt.types.
    const types = dt.types;
    const typeList: string[] = types
      ? (Array.isArray(types) ? (types as string[]) : Array.from(types as Iterable<string>))
      : [];
    if (!typeList.includes(DRAG_DATA_KEY)) return;

    // Allow the drop anywhere on the page. Specific drop zones (battlefield
    // play, minion/placeholder reposition) run their own drop handlers first
    // (they call preventDefault/stopPropagation), so this handler only ever
    // receives drops the specific zones did not claim.
    e.preventDefault();
    dt.dropEffect = 'move';

    this._updateSellZoneHighlight(e.clientX, e.clientY);

    // Widened board highlight: the board's drop zone is full screen width
    // (see _getBoardDropZone). Releases over the container itself are
    // highlighted by the container's own dragover; this covers the arena
    // backdrop beside the (narrower, centered) card row.
    const dragData = this._resolveDragData(e);
    if (dragData && this._options.isDragEnabled) {
      const board = dragData.source === 'hand'
        ? (document.getElementById('battlefield') as HTMLElement | null)
        : this._dragSourceElement?.parentElement ?? null;
      if (board) {
        const zone = this._getBoardDropZone(board);
        const overContainer = e.target instanceof Node && board.contains(e.target);
        if (zone && !overContainer && e.clientY >= zone.top && e.clientY <= zone.bottom) {
          if (dragData.source === 'hand') {
            const fieldSpell = this._isFieldSpell(dragData);
            // FULL board: no play highlight (the drop is rejected) — but
            // the RED "No space" indicator tracks the pointer (task 175).
            board.classList.toggle(DROP_TARGET_CLASS, !fieldSpell && !this._isBoardFull(board));
            // FIELD-targeting spell: the WHOLE board zone glows green (the
            // exact cast area — task 178); other drags keep no zone.
            this._updateSpellBoardZone(board, fieldSpell && this._options.isDragEnabled);
            // SPELLS cast on release — no insertion slot (the placeholder is
            // a PLAY affordance; a spell never lands on the board).
            if (dragData.minionType !== SPELL_MINION_TYPE) {
              this._updateHandDropPlaceholder(board, e.clientX);
            }
          } else {
            // Reposition: keep the drop-slot placeholder under the cursor
            // (it exists for battlefield drags — created in _startDrag).
            this._movePlaceholderForX(board, e.clientX);
          }
        }
      }
    }
  };

  /** Document-level drop for the sell drop zone (bound for dispose()). */
  private _onGlobalDrop = (e: DragEvent): void => {
    const dragData = this._parseDragData(e);
    if (!dragData) return;

    // A specific zone claimed this drop (battlefield play/reposition) —
    // it set the marker in its own drop handler. (Minion/placeholder
    // targets also stopPropagation and never reach us.)
    if ((e as unknown as Record<string, unknown>)[DROP_HANDLED_MARKER] === true) return;

    // Widened board drop zone: a release at the board's height, OUTSIDE the
    // card row (over the arena backdrop), is a board drop — the container's
    // own handler only sees releases over the container. (Over-container
    // releases are claimed by the container/minion/placeholder handlers and
    // never reach here.) The board is claimed BEFORE the sell zone: a board
    // release never sells.
    const board = dragData.source === 'hand'
      ? (document.getElementById('battlefield') as HTMLElement | null)
      : this._dragSourceElement?.parentElement ?? null;
    const boardZone = board ? this._getBoardDropZone(board) : null;
    if (board && boardZone && e.clientY >= boardZone.top && e.clientY <= boardZone.bottom) {
      e.preventDefault();
      this._clearSellZoneHighlight();
      if (this._options.isDragEnabled) {
        if (dragData.source === 'hand') {
          const handContainer = document.getElementById('hand');
          const handCardEls = handContainer?.querySelectorAll('.hand-card:not([data-overdraw-ghost])') ?? [];
          const handIds = Array.from(handCardEls).map((el: Element) => (el as HTMLElement).dataset.instanceId).filter(Boolean);
          // SPELL: cast from the widened board zone (targeted spells need a
          // minion under the pointer — over the arena backdrop there is
          // none, so a targeted cast off the card row does nothing).
          if (dragData.minionType === SPELL_MINION_TYPE && this._options.onPlaySpell &&
              handIds.includes(dragData.instanceId)) {
            const spellSpec = dragData.baseMinionId ? getSpellDefinition(dragData.baseMinionId) : null;
            if (spellSpec && spellSpec.target === 'minion') {
              const targetId = this._spellTargetAt(e.clientX, e.clientY);
              if (targetId) {
                console.log(`[DnD] Cast targeted spell on minion (HTML5, widened zone): ${dragData.instanceId} -> ${targetId}`);
                this._options.onPlaySpell(dragData.instanceId, targetId, { x: e.clientX, y: e.clientY });
              }
            } else {
              console.log(`[DnD] Cast battlefield spell (HTML5, widened zone): ${dragData.instanceId}`);
              this._options.onPlaySpell(dragData.instanceId, undefined, { x: e.clientX, y: e.clientY });
            }
          } else {
            if (handIds.includes(dragData.instanceId) && !this._isBoardFull(board)) {
              console.log(`[DnD] Drop on widened board zone (HTML5, play): ${dragData.instanceId}`);
              const targetPosition = this._calculateBattlefieldDropPosition(board, e.clientX);
              this._options.onPlayCard(dragData.instanceId, targetPosition);
            }
          }
        } else {
          console.log(`[DnD] Drop on widened board zone (HTML5, reposition): ${dragData.instanceId}`);
          const targetIndex = this._calculateBattlefieldDropPosition(board, e.clientX);
          this._options.onReposition(dragData.instanceId, targetIndex);
        }
      }
      this._currentDrag = null;
      return;
    }

    e.preventDefault();
    this._clearSellZoneHighlight();

    const inSellZone = e.clientY < this._getSellZoneBottom();
    // COOP split zone: the right half GIVES (the left half sells). A
    // non-coop zone (giveZoneEnabled off) sells the whole width.
    const onGiveSide = this._isGiveSide(e.clientX);
    if (this._options.isDragEnabled && inSellZone && onGiveSide && this._options.onGiveCard) {
      console.log(`[DnD] Drop on give zone (HTML5): ${dragData.source}/${dragData.instanceId}`);
      this._options.onGiveCard(dragData.instanceId);
      this._currentDrag = null;
    } else if (this._options.isDragEnabled && inSellZone && !onGiveSide && this._options.onSellCard) {
      console.log(`[DnD] Drop on sell zone (HTML5): ${dragData.source}/${dragData.instanceId}`);
      this._options.onSellCard(dragData.instanceId);
      this._currentDrag = null;
    }
  };

  /** Document-level dragend for the sell drop zone (bound for dispose()). */
  private _onGlobalDragEnd = (): void => {
    // Covers cancelled drags (released outside any drop target) and
    // drags that ended after a handled drop.
    this._clearSellZoneHighlight();
    // A cancelled/finished spell drag leaves no dragleave on the last
    // hovered minion — clear every spell-target glow (dragend always fires,
    // dragleave does not on a drop outside the card).
    this._clearSpellTargetGlows();
    // The field-spell cast zone dies with the drag (task 178) — dragend
    // fires for BOTH cancelled drags and handled drops.
    this._spellZoneEl?.classList.remove('visible');
  };

  /** Removes the spell-target glow from every battlefield minion. */
  private _clearSpellTargetGlows(): void {
    document
      .querySelectorAll(`.battlefield-minion.${SPELL_TARGET_GLOW_CLASS}`)
      .forEach((el) => el.classList.remove(SPELL_TARGET_GLOW_CLASS));
  }

  // Touch-specific state
  private _touchGhost: HTMLElement | null = null;
  private _touchStartX = 0;
  private _touchStartY = 0;
  private _touchIsDragging = false;
  private _touchDragData: DragData | null = null;
  /**
   * LONG-PRESS DRAG LOCK: once a touch press has stayed still for
   * LONG_PRESS_TOUCH_MS (the CardInspectService preview just fired — the
   * user is reading the card), the rest of that touch sequence can never
   * start a drag. A held finger drifts: before this lock, a creeping
   * long-press crossed TOUCH_DRAG_THRESHOLD after the preview fired and
   * became an accidental drag — released over the sell zone, the card was
   * SOLD while the user only wanted to read it.
   */
  private _touchLongPressLocked = false;
  private _touchLongPressLockTimer: number | null = null;

  // ─── Mouse drag (the DESKTOP path) ───────────────────────────────────────────
  // The desktop mouse drag is PURE JS (mousedown/mousemove/mouseup), NOT the
  // browser's native HTML5 drag. The native machinery (the browser's built-in
  // listeners on `draggable` elements) started the drag only ~50% of the time
  // on desktop — its initiation is lost whenever the pointer starts on a
  // child with different native draggability (the card art <img draggable=
  // "false"> vs the card body) or while the browser's pending selection /
  // double-click state from the previous click is still active. The JS path
  // resolves the target itself (closest .hand-card/.battlefield-minion) and
  // preventDefault()s the mousedown, so the browser's built-in drag
  // listeners never get a chance to run. It reuses the EXACT drop-resolution
  // of the touch path (_resolveDropAtRelease) — one set of rules for both
  // pointers, which is why mobile (touch) always worked.
  private _mouseStartX = 0;
  private _mouseStartY = 0;
  private _mouseIsDragging = false;
  /** True while a left-button mouse press on a card is being tracked (pre-drag). */
  private _mousePressActive = false;

  constructor(options: DragAndDropOptions) {
    this._options = options;
    this._installGlobalSellDropZone();
  }

  // ─── Sell Drop Zone (area above the battlefield) ────────────────────────

  /**
   * Installs the document-level dragover/drop/dragend listeners that implement
   * the sell drop zone: the area ABOVE the battlefield (play area).
   *
   * Any card — from the hand OR from the battlefield — can be dragged up into
   * that area and released to sell it. The zone is a visual + hit-test
   * convenience; the authoritative validation happens server-side (the
   * SellMinionRequest is only accepted during the preparation phase).
   *
   * Priority rules (specific zones win over the sell zone):
   * - Hand card dropped on the battlefield container → PLAY (never a sell).
   * - Battlefield minion dropped on another minion/placeholder → REPOSITION.
   * - Battlefield minion dropped back on its own board → no-op (cancelled),
   *   NOT a sell — avoids accidental sales when the user drops the card
   *   where it came from.
   * - Otherwise, release above the battlefield → SELL. Release below it →
   *   no-op.
   */
  private _installGlobalSellDropZone(): void {
    if (this._globalSellZoneInstalled || typeof document === 'undefined') return;
    this._globalSellZoneInstalled = true;

    document.addEventListener('dragover', this._onGlobalDragOver);
    document.addEventListener('drop', this._onGlobalDrop);
    document.addEventListener('dragend', this._onGlobalDragEnd);

    // The JS MOUSE drag (desktop): the press starts on the card element
    // (see _onCardMouseDown), the move/up are tracked document-level so the
    // drag survives the pointer leaving the card mid-gesture.
    document.addEventListener('mousemove', this._onGlobalMouseMove);
    document.addEventListener('mouseup', this._onGlobalMouseUp);
    window.addEventListener('blur', this._onMouseCancel);
    document.addEventListener('contextmenu', this._onMouseCancel);
  }

  /**
   * Removes the document-level sell-zone listeners and the overlay element.
   * Safe to call multiple times.
   *
   * Important: main.ts creates a fresh DragAndDropService for every new app
   * shell (login/re-login, restart after game over). Without dispose(), the
   * previous shells' document-level listeners would accumulate and every
   * drop would be processed by all old shells (stale callbacks, duplicate
   * sell requests).
   */
  dispose(): void {
    if (this._globalSellZoneInstalled) {
      document.removeEventListener('dragover', this._onGlobalDragOver);
      document.removeEventListener('drop', this._onGlobalDrop);
      document.removeEventListener('dragend', this._onGlobalDragEnd);
      document.removeEventListener('mousemove', this._onGlobalMouseMove);
      document.removeEventListener('mouseup', this._onGlobalMouseUp);
      window.removeEventListener('blur', this._onMouseCancel);
      document.removeEventListener('contextmenu', this._onMouseCancel);
      this._globalSellZoneInstalled = false;
    }
    if (this._sellZone) {
      this._sellZone.remove();
      this._sellZone = null;
    }
  }

  /**
   * Returns (creating on first use) the visual sell-zone overlay element.
   * In a COOP team match (giveZoneEnabled) the zone is split into two
   * equal halves: the LEFT half sells (red) and the RIGHT half gives the
   * minion to the teammate for 5 gold (gold/yellow). Non-coop matches keep
   * the single full-width sell label.
   */
  private _getOrCreateSellZone(): HTMLElement {
    if (!this._sellZone) {
      const zone = document.createElement('div');
      zone.className = SELL_ZONE_CLASS;
      document.body.appendChild(zone);
      this._sellZone = zone;
    }
    // Build/rebuild the label(s) to match the current give-zone mode (a
    // fresh zone starts unlabeled; a mode change swaps the layout — a coop
    // player reusing a non-coop zone element and vice versa).
    const shouldSplit = !!this._options.giveZoneEnabled;
    const isSplit = this._sellZone.querySelector('.sell-zone-half') !== null;
    if (shouldSplit !== isSplit || this._sellZone.children.length === 0) {
      this._sellZone.innerHTML = '';
      if (shouldSplit) {
        const left = document.createElement('div');
        left.className = 'sell-zone-half sell-half';
        const leftLabel = document.createElement('span');
        leftLabel.className = 'sell-zone-label';
        leftLabel.textContent = '🪙 Sell';
        left.appendChild(leftLabel);
        const right = document.createElement('div');
        right.className = 'sell-zone-half give-half';
        const rightLabel = document.createElement('span');
        rightLabel.className = 'sell-zone-label give-zone-label';
        rightLabel.textContent = `🤝 Give (${COOP_GIVE_COST}🪙)`;
        right.appendChild(rightLabel);
        this._sellZone.appendChild(left);
        this._sellZone.appendChild(right);
      } else {
        const label = document.createElement('span');
        label.className = 'sell-zone-label';
        label.textContent = '🪙 Drag here to sell';
        this._sellZone.appendChild(label);
      }
    }
    return this._sellZone;
  }

  /**
   * True when a drop at `clientX` lands on the GIVE half of the split
   * coop sell zone (the right half). False when the give zone is off.
   */
  private _isGiveSide(clientX: number): boolean {
    if (!this._options.giveZoneEnabled) return false;
    return clientX >= (typeof window !== 'undefined' ? window.innerWidth : 0) / 2;
  }

  /**
   * Shows the sell-zone highlight when the pointer is inside the sell zone
   * (above the battlefield, during an enabled drag), hides it otherwise.
   * The overlay is resized to exactly the sell zone so it can never cover
   * the battlefield (play area).
   */
  /**
   * Shows the sell zone's BASE highlight for the duration of a live drag —
   * the user must be able to see the sell boundary the whole time a card is
   * being dragged/moved, not only while the pointer is inside the zone.
   * (Pointer position refines it to the strong .active state via
   * _updateSellZoneHighlight / _updateTouchDropHighlight.)
   */
  private _showSellZoneBase(): void {
    if (!this._options.isDragEnabled) return;
    const zone = this._getOrCreateSellZone();
    zone.style.height = `${this._getSellZoneBottom()}px`;
    zone.classList.add(SELL_ZONE_VISIBLE_CLASS);
  }

  /**
   * Keeps the sell zone visible during the live drag and toggles the strong
   * highlight while the pointer is inside the sell zone.
   */
  private _updateSellZoneHighlight(clientX: number, clientY: number): void {
    if (!this._options.isDragEnabled) {
      this._clearSellZoneHighlight();
      return;
    }
    const sellZoneBottom = this._getSellZoneBottom();
    const zone = this._getOrCreateSellZone();
    zone.style.height = `${sellZoneBottom}px`;
    zone.classList.add(SELL_ZONE_VISIBLE_CLASS);
    const inside = clientY < sellZoneBottom;
    zone.classList.toggle(SELL_ZONE_ACTIVE_CLASS, inside);
    // Split coop zone: highlight the half under the pointer.
    if (this._options.giveZoneEnabled) {
      const give = this._isGiveSide(clientX);
      for (const half of zone.querySelectorAll<HTMLElement>('.sell-zone-half')) {
        half.classList.toggle(SELL_ZONE_ACTIVE_CLASS, inside && (half.classList.contains('give-half') === give));
      }
    }
  }

  /**
   * Bottom edge (viewport Y) of the sell drop zone.
   *
   * The boundary is anchored to the TOP OF THE PLAYED CARDS (the
   * #battlefield's .battlefield-minion elements) MINUS SELL_ZONE_CARD_OFFSET_PX
   * ("about 10px above the edge of the played cards"), derived from the LIVE
   * card geometry — so it holds on every screen size. The container's own
   * top edge is NOT a stable anchor: it sits above the cards (padding) and,
   * on an empty board, the min-height container floats with the arena
   * height (screen-size dependent).
   *
   * Fallbacks: empty board → the container's top edge; #battlefield absent
   * or without layout (top === 0, e.g. in jsdom tests) → the top half of
   * the viewport (the legacy behavior).
   */
  private _getSellZoneBottom(): number {
    if (typeof document === 'undefined') return window.innerHeight / 2;
    const battlefield = document.getElementById('battlefield');
    if (battlefield) {
      let cardTop = Infinity;
      const minions = battlefield.querySelectorAll<HTMLElement>('.battlefield-minion');
      for (const m of minions) {
        const t = m.getBoundingClientRect().top;
        if (t > 0) cardTop = Math.min(cardTop, t);
      }
      if (Number.isFinite(cardTop)) return cardTop - SELL_ZONE_CARD_OFFSET_PX;
      const top = battlefield.getBoundingClientRect().top;
      if (top > 0) return top;
    }
    return window.innerHeight / 2;
  }

  private _clearSellZoneHighlight(): void {
    if (this._sellZone) {
      this._sellZone.classList.remove(SELL_ZONE_ACTIVE_CLASS);
      this._sellZone.classList.remove(SELL_ZONE_VISIBLE_CLASS);
      for (const half of this._sellZone.querySelectorAll<HTMLElement>('.sell-zone-half')) {
        half.classList.remove(SELL_ZONE_ACTIVE_CLASS);
      }
    }
  }

  updateOptions(options: Partial<DragAndDropOptions>): void {
    this._options = { ...this._options, ...options };
  }

  get isDragEnabled(): boolean {
    return this._options.isDragEnabled;
  }

  /**
   * Reset all internal drag state and clear any DOM artifacts.
   */
  reset(): void {
    this._currentDrag = null;
    this._dragSourceElement = null;
    this._touchDragData = null;
    this._touchIsDragging = false;
    this._mousePressActive = false;
    this._mouseIsDragging = false;
    this._removePlaceholder();
    this._removeHandDropPlaceholder();
    this._removeTouchGhost();
    this._clearSellZoneHighlight();

    const draggingEls = document.querySelectorAll(`.${DRAGGING_CLASS}`);
    for (const el of draggingEls) {
      el.classList.remove(DRAGGING_CLASS);
      (el as HTMLElement).style.opacity = '';
      (el as HTMLElement).style.pointerEvents = '';
    }
    const dropTargets = document.querySelectorAll(`.${DROP_TARGET_CLASS}`);
    for (const el of dropTargets) {
      el.classList.remove(DROP_TARGET_CLASS);
      (el as HTMLElement).style.borderLeft = '';
      (el as HTMLElement).style.borderRight = '';
    }
    const selectedEls = document.querySelectorAll('.selected');
    for (const el of selectedEls) {
      el.classList.remove('selected');
    }
  }

  // ─── Hand Card Drag Setup ────────────────────────────────────────────────

  makeHandCardDraggable(
    element: HTMLElement,
    instanceId: string,
    index: number,
    card?: { baseMinionId?: string; minionType?: string },
  ): void {
    // The card is NOT made natively draggable here (the old
    // setAttribute('draggable','true') re-armed the browser's built-in HTML5
    // drag listeners — the ~50 % "nothing happens" initiation bug). The
    // desktop mouse drag is pure JS (see _onCardMouseDown); the HTML5
    // listeners below stay as a harmless fallback for synthetic events and
    // tests (they can never fire natively without the attribute).

    element.addEventListener('dragstart', (e: DragEvent) => {
      if (!this._options.isDragEnabled) { e.preventDefault(); return; }
      console.log(`[DnD] Drag start (HTML5): hand/${instanceId}`);
      this._startDrag(e, 'hand', instanceId, index, element, undefined, card);
    });
    element.addEventListener('dragend', () => {
      console.log(`[DnD] Drag end (HTML5): hand/${instanceId}`);
      this._endDrag('hand', instanceId, index);
    });

    // Touch drag (mobile)
    // Mouse drag (desktop) — pure JS, see _onCardMouseDown.
    element.addEventListener('mousedown', (e: MouseEvent) => this._onCardMouseDown(e, 'hand', instanceId, index, element, card));

    element.addEventListener('touchstart', (e: TouchEvent) => this._onTouchStart(e, 'hand', instanceId, index, element, card), { passive: false });
    element.addEventListener('touchmove', (e: TouchEvent) => this._onTouchMove(e), { passive: false });
    element.addEventListener('touchend', (e: TouchEvent) => this._onTouchEnd(e));
    element.addEventListener('touchcancel', () => this._onTouchCancel());
  }

  // ─── Battlefield Minion Drag Setup ───────────────────────────────────────

  makeBattlefieldMinionDraggable(
    element: HTMLElement,
    instanceId: string,
    index: number,
    battlefieldContainer: HTMLElement,
  ): void {
    // NOT natively draggable (see makeHandCardDraggable): the desktop mouse
    // drag is pure JS; the HTML5 listeners below are a fallback for
    // synthetic events/tests only.

    element.addEventListener('dragstart', (e: DragEvent) => {
      if (!this._options.isDragEnabled) { e.preventDefault(); return; }
      this._startDrag(e, 'battlefield', instanceId, index, element, battlefieldContainer);
    });
    element.addEventListener('dragend', () => {
      this._endDrag('battlefield', instanceId, index);
      this._clearDropTargets(battlefieldContainer);
    });

    // Touch drag (mobile)
    // Mouse drag (desktop) — pure JS, see _onCardMouseDown.
    element.addEventListener('mousedown', (e: MouseEvent) => this._onCardMouseDown(e, 'battlefield', instanceId, index, element));

    element.addEventListener('touchstart', (e: TouchEvent) => this._onTouchStart(e, 'battlefield', instanceId, index, element), { passive: false });
    element.addEventListener('touchmove', (e: TouchEvent) => this._onTouchMove(e), { passive: false });
    element.addEventListener('touchend', (e: TouchEvent) => this._onTouchEnd(e));
    element.addEventListener('touchcancel', () => this._onTouchCancel());
  }

  // ─── Drop Zone Setup ─────────────────────────────────────────────────────

  setupBattlefieldDropZone(
    battlefieldContainer: HTMLElement,
    getHandCardIds: () => string[],
    _currentBattlefieldIds: string[], // kept for API compat, not used
  ): void {
    // HTML5 dragover/drop (desktop)
    battlefieldContainer.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
      // NOTE: _resolveDragData, not _parseDragData — the dataTransfer is
      // protected (empty) during dragover in real browsers.
      const dragData = this._resolveDragData(e);
      if (!dragData) return;
      if (dragData.source === 'battlefield') {
        // Reposition drag over the board itself (the gaps between/around
        // the cards — minion/placeholder dragovers stopPropagation and own
        // their regions). Keep the placeholder (the visible drop slot) under
        // the cursor: drops in those gaps are valid repositions (see the
        // container's drop handler below), so the visual must track them.
        this._movePlaceholderForX(battlefieldContainer, e.clientX);
        return;
      }

      const dt = e.dataTransfer as DataTransfer;
      const handIds = getHandCardIds();

      // SPELL: the cast affordance — the board glows (the spell goes into
      // play, no insertion slot), and a TARGETED spell additionally outlines
      // the specific minion under the pointer (its cast target).
      if (dragData.minionType === SPELL_MINION_TYPE) {
        const canCast = this._options.isDragEnabled && handIds.includes(dragData.instanceId);
        dt.dropEffect = canCast ? 'move' : 'none';
        this._removeHandDropPlaceholder();
        for (const m of battlefieldContainer.querySelectorAll<HTMLElement>('.battlefield-minion')) {
          m.classList.remove(DROP_TARGET_CLASS);
        }
        if (this._isFieldSpell(dragData)) {
          // FIELD-targeting spell: the WHOLE board zone glows green — the
          // full-area cast indicator replaces the narrow row glow (task 178).
          battlefieldContainer.classList.remove(DROP_TARGET_CLASS);
          this._updateSpellBoardZone(battlefieldContainer, canCast);
        } else {
          // Minion-targeting spell: the row glows + the hovered minion is
          // outlined (its cast target) — no full-area zone.
          battlefieldContainer.classList.toggle(DROP_TARGET_CLASS, canCast);
          this._updateSpellBoardZone(battlefieldContainer, false);
        }
        if (canCast && dragData.baseMinionId &&
            getSpellDefinition(dragData.baseMinionId)?.target === 'minion') {
          const hovered = (e.target as Element | null)?.closest?.('.battlefield-minion') as HTMLElement | null;
          hovered?.classList.add(DROP_TARGET_CLASS);
        }
        return;
      }

      const canDrop = this._options.isDragEnabled &&
        handIds.includes(dragData.instanceId) &&
        !this._isBoardFull(battlefieldContainer);
      dt.dropEffect = canDrop ? 'move' : 'none';
      battlefieldContainer.classList.toggle(DROP_TARGET_CLASS, canDrop);
      // A normal card drag over the board never shows the spell zone.
      this._updateSpellBoardZone(battlefieldContainer, false);
      // ALWAYS show the slot indicator for a hand drag over the board:
      // green "Drop here" while there is space, RED "No space" when the
      // board is full (playtest, task 175 — the old code showed the green
      // slot on a full board because the capacity check was hardcoded < 8
      // vs the real limit of 7).
      this._updateHandDropPlaceholder(battlefieldContainer, e.clientX);
    });

    battlefieldContainer.addEventListener('dragleave', (e: DragEvent) => {
      if (!battlefieldContainer.contains(e.relatedTarget as Node)) {
        battlefieldContainer.classList.remove(DROP_TARGET_CLASS);
        this._removeHandDropPlaceholder();
        this._updateSpellBoardZone(battlefieldContainer, false);
      }
    });

    battlefieldContainer.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      // Claim this drop: the document-level sell zone must not also treat
      // a drop on the battlefield as a sell (play wins, or the play was
      // rejected — neither case is a sale).
      (e as unknown as Record<string, unknown>)[DROP_HANDLED_MARKER] = true;
      battlefieldContainer.classList.remove(DROP_TARGET_CLASS);
      this._removeHandDropPlaceholder();
      this._updateSpellBoardZone(battlefieldContainer, false);

      const dragData = this._resolveDragData(e);
      console.log(`[DnD] Drop on battlefield (HTML5): dragData=${dragData?.source}/${dragData?.instanceId}`);
      if (!dragData) return;

      if (dragData.source === 'battlefield') {
        // Reposition: the release point is on the board but NOT on a
        // specific minion/placeholder (those claim their own drops and
        // stopPropagation). Compute the insertion index from the release X
        // so the WHOLE board — including the gaps between cards — is a
        // valid drop zone. Previously these drops were claimed here and
        // silently dropped, so repositions only worked when the user
        // released exactly on top of another card ("snap back" bug).
        if (this._options.isDragEnabled) {
          const targetIndex = this._calculateBattlefieldDropPosition(battlefieldContainer, e.clientX);
          console.log(`[DnD] Reposition (container drop): ${dragData.instanceId} -> index ${targetIndex}`);
          this._options.onReposition(dragData.instanceId, targetIndex);
          this._currentDrag = null;
        }
        return;
      }

      const handIds = getHandCardIds();

      // SPELL: a hand card that is a spell CASTS on board release (it never
      // lands on the board). Targeted spells need the release on a specific
      // minion (the drop's target element — the card was dragged onto it);
      // released off-target they do nothing. Battlefield spells cast from
      // anywhere over the board (the whole arena band, like a play).
      if (dragData.minionType === SPELL_MINION_TYPE && this._options.isDragEnabled &&
          handIds.includes(dragData.instanceId) && this._options.onPlaySpell) {
        const spellSpec = dragData.baseMinionId ? getSpellDefinition(dragData.baseMinionId) : null;
        if (spellSpec && spellSpec.target === 'minion') {
          const minionEl = (e.target as Element | null)?.closest?.('.battlefield-minion') as HTMLElement | null;
          const targetId = minionEl?.dataset.instanceId;
          if (targetId) {
            console.log(`[DnD] Cast targeted spell on minion (HTML5): ${dragData.instanceId} -> ${targetId}`);
            this._options.onPlaySpell(dragData.instanceId, targetId, { x: e.clientX, y: e.clientY });
            this._currentDrag = null;
          }
        } else {
          console.log(`[DnD] Cast battlefield spell (HTML5): ${dragData.instanceId}`);
          this._options.onPlaySpell(dragData.instanceId, undefined, { x: e.clientX, y: e.clientY });
          this._currentDrag = null;
        }
        return;
      }

      const bfMinions = battlefieldContainer.querySelectorAll('.battlefield-minion');
      const canPlay = this._options.isDragEnabled &&
        handIds.includes(dragData.instanceId) &&
        !this._isBoardFull(battlefieldContainer);
      console.log(`[DnD] canPlay=${canPlay}, handIds=${handIds}, bfCount=${bfMinions.length}, dragEnabled=${this._options.isDragEnabled}`);
      if (canPlay) {
        // Calculate target position based on drop X coordinate
        const targetPosition = this._calculateBattlefieldDropPosition(battlefieldContainer, e.clientX);
        this._options.onPlayCard(dragData.instanceId, targetPosition);
        this._currentDrag = null;
      }
    });

    // Touch drop zone: handled in _onTouchEnd via elementFromPoint
  }

  setupBattlefieldMinionDropTarget(
    element: HTMLElement,
    instanceId: string,
    index: number,
    allMinionIds: string[],
  ): void {
    // HTML5 dragover/drop for reposition (desktop)
    element.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
      const dragData = this._resolveDragData(e);
      if (!dragData) return;
      if (dragData.source === 'hand') {
        // SPELL TARGET GLOW: a minion-target spell dragged over this minion
        // glows it gold (the valid cast target). The battlefield (PLAY) drop
        // zone still owns the drop — do NOT stopPropagation (its dragover
        // keeps running, no reposition placeholder for hand drags).
        if (dragData.minionType === SPELL_MINION_TYPE) {
          const spec = dragData.baseMinionId ? getSpellDefinition(dragData.baseMinionId) : null;
          if (spec?.target === 'minion') {
            element.classList.add(SPELL_TARGET_GLOW_CLASS);
          }
        }
        return;
      }
      e.stopPropagation();
      if (dragData.instanceId === instanceId) return;

      const dt = e.dataTransfer as DataTransfer;
      dt.dropEffect = 'move';

      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const isRightHalf = x > rect.width / 2;

      element.classList.add(DROP_TARGET_CLASS);
      element.style.borderLeft = isRightHalf ? '' : '3px solid #f1c40f';
      element.style.borderRight = !isRightHalf ? '3px solid #f1c40f' : '';

      if (this._placeholder) {
        const battlefield = element.parentElement;
        if (battlefield) {
          const children = Array.from(battlefield.children).filter(
            (c) => !c.classList.contains(PLACEHOLDER_CLASS)
          );
          const targetChildIndex = children.indexOf(element);
          const insertIndex = isRightHalf ? targetChildIndex + 1 : targetChildIndex;
          if (insertIndex < children.length) {
            battlefield.insertBefore(this._placeholder, children[insertIndex]);
          } else {
            battlefield.appendChild(this._placeholder);
          }
        }
      }
    });

    element.addEventListener('dragleave', (e: DragEvent) => {
      element.classList.remove(DROP_TARGET_CLASS);
      element.classList.remove(SPELL_TARGET_GLOW_CLASS);
      element.style.borderLeft = '';
      element.style.borderRight = '';
    });

    element.addEventListener('drop', (e: DragEvent) => {
      const dragData = this._resolveDragData(e);
      // Hand drags (and drags this service did not start) must BUBBLE up to
      // the battlefield's play drop handler — never claim them here. A hand
      // card released over an existing minion is a PLAY; swallowing the
      // drop (the old behavior) made card plays "randomly" fail whenever
      // the release point happened to land on a minion.
      if (!dragData || dragData.source !== 'battlefield') return;
      e.preventDefault();
      e.stopPropagation();
      (e as unknown as Record<string, unknown>)[DROP_HANDLED_MARKER] = true;
      element.classList.remove(DROP_TARGET_CLASS);
      element.style.borderLeft = '';
      element.style.borderRight = '';

      if (dragData.instanceId === instanceId) return;

      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const isRightHalf = x > rect.width / 2;

      const targetChildIndex = allMinionIds.indexOf(instanceId);
      const targetIndex = isRightHalf ? targetChildIndex + 1 : targetChildIndex;

      this._options.onReposition(dragData.instanceId, targetIndex);
      this._currentDrag = null;
    });

    // Touch reposition: handled in _onTouchEnd via elementFromPoint
  }

  // ─── Internal: HTML5 Drag Helpers ────────────────────────────────────────

  private _startDrag(
    e: DragEvent,
    source: DragSource,
    instanceId: string,
    index: number,
    element: HTMLElement,
    battlefieldContainer?: HTMLElement,
    card?: { baseMinionId?: string; minionType?: string },
  ): void {
    const dragData: DragData = {
      source, instanceId, originalIndex: index,
      baseMinionId: card?.baseMinionId, minionType: card?.minionType,
    };
    this._currentDrag = dragData;
    this._dragSourceElement = element;

    const dt = e.dataTransfer as DataTransfer;
    if (dt) {
      dt.setData(DRAG_DATA_KEY, JSON.stringify(dragData));
      dt.effectAllowed = 'move';
      this._setDragImage(e, element);
    }

    element.classList.add(DRAGGING_CLASS);
    element.style.opacity = '0.4';

    if (source === 'battlefield' && battlefieldContainer) {
      this._createPlaceholder(battlefieldContainer, element);
    }

    // The drag is live: the sell zone is visible for the whole drag (the
    // user must see where the sell boundary is), not only inside it.
    this._showSellZoneBase();

    this._options.onDragStart?.(dragData, element);
  }

  private _endDrag(source: DragSource, instanceId: string, index: number): void {
    if (this._dragSourceElement) {
      this._dragSourceElement.classList.remove(DRAGGING_CLASS);
      this._dragSourceElement.style.opacity = '';
      this._dragSourceElement.style.pointerEvents = '';
      this._dragSourceElement = null;
    }
    this._currentDrag = null;
    this._removePlaceholder();

    this._options.onDragEnd?.({ source, instanceId, originalIndex: index }, false);
  }

  // ─── Touch Drag Implementation ───────────────────────────────────────────

  private _onTouchStart(e: TouchEvent, source: DragSource, instanceId: string, index: number, element: HTMLElement, card?: { baseMinionId?: string; minionType?: string }): void {
    if (!this._options.isDragEnabled) {
      console.log('[DnD] Touch start blocked: isDragEnabled=false');
      return;
    }

    const touch = e.touches[0];
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
    this._touchIsDragging = false;
    this._touchLongPressLocked = false;
    this._touchDragData = {
      source, instanceId, originalIndex: index,
      baseMinionId: card?.baseMinionId, minionType: card?.minionType,
    };
    this._dragSourceElement = element;
    // If this press stays still for the full long-press duration, it is a
    // "read" gesture — lock the sequence against starting a drag (see
    // _touchLongPressLocked).
    this._armLongPressLock();
    console.log(`[DnD] Touch start: ${source}/${instanceId} at ${touch.clientX},${touch.clientY}`);

    // Prevent default to avoid scrolling while dragging
    // (only if this looks like it might become a drag)
  }

  /** Arms (re-arms) the long-press lock for the active touch press. */
  private _armLongPressLock(): void {
    this._clearLongPressLockTimer();
    this._touchLongPressLockTimer = window.setTimeout(() => {
      this._touchLongPressLockTimer = null;
      this._touchLongPressLocked = true;
    }, LONG_PRESS_TOUCH_MS);
  }

  private _clearLongPressLockTimer(): void {
    if (this._touchLongPressLockTimer !== null) {
      clearTimeout(this._touchLongPressLockTimer);
      this._touchLongPressLockTimer = null;
    }
  }

  private _onTouchMove(e: TouchEvent): void {
    if (!this._touchDragData || !this._dragSourceElement) return;

    const touch = e.touches[0];
    const dx = touch.clientX - this._touchStartX;
    const dy = touch.clientY - this._touchStartY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Only start dragging after threshold (distinguish from tap). The
    // long-press lock refuses to start a drag once the press has been still
    // for the full LONG_PRESS_TOUCH_MS — that press is a completed
    // long-press (preview up, user reading), and a finger drift must not
    // turn it into a drag (the "long-press sells my card" bug).
    if (!this._touchIsDragging && !this._touchLongPressLocked && distance > TOUCH_DRAG_THRESHOLD) {
      this._touchIsDragging = true;
      e.preventDefault();

      // Visual feedback — also disable pointer-events so elementFromPoint sees through it
      this._dragSourceElement.classList.add(DRAGGING_CLASS);
      this._dragSourceElement.style.opacity = '0.4';
      this._dragSourceElement.style.pointerEvents = 'none';

      // Create touch ghost
      this._createTouchGhost(this._dragSourceElement, touch.clientX, touch.clientY);

      // Create placeholder for battlefield reposition
      if (this._touchDragData.source === 'battlefield') {
        const battlefield = this._dragSourceElement.parentElement;
        if (battlefield) {
          this._createPlaceholder(battlefield, this._dragSourceElement);
        }
      }

      // The drag is live: the sell zone is visible for the whole drag (the
      // user must see where the sell boundary is), not only inside it.
      this._showSellZoneBase();

      this._options.onDragStart?.(this._touchDragData, this._dragSourceElement);
    }

    if (this._touchIsDragging) {
      e.preventDefault();

      // Move ghost
      if (this._touchGhost) {
        this._touchGhost.style.left = (touch.clientX - this._touchGhost.offsetWidth / 2) + 'px';
        this._touchGhost.style.top = (touch.clientY - this._touchGhost.offsetHeight / 2) + 'px';
      }

      // Find element under finger for drop zone highlight
      this._updateTouchDropHighlight(touch.clientX, touch.clientY);
      // SPELL TARGET GLOW (the touch mirror of the dragover glow): a
      // minion-target spell highlights the minion under the finger.
      this._updateSpellTargetGlow(touch.clientX, touch.clientY);
    }
  }

  /**
   * Toggles the spell-target glow for a touch drag: a minion-target spell
   * highlights the minion under the finger (the valid cast target); every
   * other drag state clears all glows. (Desktop uses the per-minion
   * dragover/dragleave instead.)
   */
  private _updateSpellTargetGlow(clientX: number, clientY: number): void {
    const drag = this._touchDragData;
    const isSpellDrag = drag?.source === 'hand' && drag.minionType === SPELL_MINION_TYPE;
    const spec = isSpellDrag && drag.baseMinionId ? getSpellDefinition(drag.baseMinionId) : null;
    if (!spec || spec.target !== 'minion') {
      this._clearSpellTargetGlows();
      return;
    }
    const targetId = this._spellTargetAt(clientX, clientY);
    document
      .querySelectorAll(`.battlefield-minion.${SPELL_TARGET_GLOW_CLASS}`)
      .forEach((el) => {
        if ((el as HTMLElement).dataset.instanceId !== targetId) {
          el.classList.remove(SPELL_TARGET_GLOW_CLASS);
        }
      });
    if (targetId) {
      document
        .querySelector(`.battlefield-minion[data-instance-id="${targetId}"]`)
        ?.classList.add(SPELL_TARGET_GLOW_CLASS);
    }
  }

  private _onTouchEnd(e: TouchEvent): void {
    if (!this._touchDragData) {
      this._cleanupTouchState();
      return;
    }

    if (this._touchIsDragging) {
      e.preventDefault();

      // Get the last touch position
      const touch = e.changedTouches[0];

      // Remove ghost BEFORE elementFromPoint — the ghost blocks hit-testing!
      this._removeTouchGhost();

      this._resolveDropAtRelease(touch.clientX, touch.clientY);
    }

    this._cleanupTouchState();
  }

  /**
   * Resolves the drop at (x, y) for the ACTIVE press — the `_touchDragData`
   * slot is shared by BOTH pointer paths (touch and the JS mouse drag), so
   * one set of drop rules serves desktop and mobile identically. The ghost
   * must already be removed (it blocks elementFromPoint hit-testing). Does
   * NOT clean up state — the caller owns that. Reports the outcome via
   * onDragEnd (succeeded = a real action was routed).
   */
  private _resolveDropAtRelease(x: number, y: number): void {
      const drag = this._touchDragData;
      if (!drag) return;

      // Find the element under the pointer at release point
      const targetElement = document.elementFromPoint(x, y);
      console.log(`[DnD] Drop at ${x},${y}, target: ${targetElement?.className ?? targetElement?.tagName}`);

      let succeeded = false;

      if (drag.source === 'hand') {
        // Check if dropped on battlefield — the board is claimed BEFORE the
        // sell zone (a release over the board never sells), and the board's
        // drop zone is WIDENED horizontally (see _isOverBoard).
        const battlefield = document.getElementById('battlefield');
        console.log(`[DnD] battlefield element: ${battlefield ? 'found' : 'null'}`);
        if (battlefield && this._isOverBoard(battlefield, targetElement, y)) {
          // Read fresh hand data
          const handContainer = document.getElementById('hand');
          const handCardEls = handContainer?.querySelectorAll('.hand-card:not([data-overdraw-ghost])') ?? [];
          const handIds = Array.from(handCardEls).map((el: Element) => (el as HTMLElement).dataset.instanceId).filter(Boolean);
          const bfMinions = battlefield.querySelectorAll('.battlefield-minion');

          // SPELL: cast on board release (same routing as the HTML5 paths).
          if (drag.minionType === SPELL_MINION_TYPE && this._options.onPlaySpell &&
              handIds.includes(drag.instanceId)) {
            const spellSpec = drag.baseMinionId ? getSpellDefinition(drag.baseMinionId) : null;
            if (spellSpec && spellSpec.target === 'minion') {
              const minionEl = targetElement?.closest?.('.battlefield-minion') as HTMLElement | null;
              const targetId = minionEl?.dataset.instanceId;
              if (targetId) {
                console.log(`[DnD] Cast targeted spell on minion: ${drag.instanceId} -> ${targetId}`);
                this._options.onPlaySpell(drag.instanceId, targetId, { x, y });
                succeeded = true;
              }
            } else {
              console.log(`[DnD] Cast battlefield spell: ${drag.instanceId}`);
              this._options.onPlaySpell(drag.instanceId, undefined, { x, y });
              succeeded = true;
            }
          } else if (this._options.isDragEnabled &&
              handIds.includes(drag.instanceId) &&
              !this._isBoardFull(battlefield)) {
            // Calculate target position based on the release X coordinate
            const targetPosition = this._calculateBattlefieldDropPosition(battlefield, x);
            this._options.onPlayCard(drag.instanceId, targetPosition);
            succeeded = true;
          }
        } else if (this._options.isDragEnabled && y < this._getSellZoneBottom()) {
          // Dropped above the battlefield (not on it) → sell… or GIVE on
          // the split zone's right half (COOP team matches).
          if (this._isGiveSide(x) && this._options.onGiveCard) {
            console.log(`[DnD] Drop on give zone: hand/${drag.instanceId}`);
            this._options.onGiveCard(drag.instanceId);
          } else if (this._options.onSellCard) {
            console.log(`[DnD] Drop on sell zone: hand/${drag.instanceId}`);
            this._options.onSellCard(drag.instanceId);
          }
          succeeded = true;
        }
      } else if (drag.source === 'battlefield') {
        const ownBattlefield = this._dragSourceElement?.parentElement ?? null;
        const overOwnBoard = this._isOverBoard(ownBattlefield, targetElement, y);
        if (overOwnBoard && this._options.isDragEnabled && ownBattlefield) {
          // Reposition: the WHOLE own board is a valid drop zone (another
          // minion, the placeholder, or the gaps between them) — the
          // insertion index is derived from the release X, mirroring the
          // HTML5 container drop handler. (Previously only a release
          // exactly on top of another minion repositioned; anything else
          // on the board was a silent no-op.)
          const targetIndex = this._calculateBattlefieldDropPosition(ownBattlefield, x);
          console.log(`[DnD] Reposition: ${drag.instanceId} -> index ${targetIndex}`);
          this._options.onReposition(drag.instanceId, targetIndex);
          succeeded = true;
        } else if (this._options.isDragEnabled && y < this._getSellZoneBottom()) {
          // Dropped above the battlefield (not on it) → sell… or GIVE on
          // the split zone's right half (COOP team matches). Dropping back
          // on one's own board repositions (handled above) — it never sells,
          // so users can't accidentally sell by dropping where they grabbed.
          if (this._isGiveSide(x) && this._options.onGiveCard) {
            console.log(`[DnD] Drop on give zone: battlefield/${drag.instanceId}`);
            this._options.onGiveCard(drag.instanceId);
          } else if (this._options.onSellCard) {
            console.log(`[DnD] Drop on sell zone: battlefield/${drag.instanceId}`);
            this._options.onSellCard(drag.instanceId);
          }
          succeeded = true;
        }
      }

      this._clearSellZoneHighlight();
      this._options.onDragEnd?.(drag, succeeded);
  }

  // ─── Mouse Drag (desktop) — the pure-JS replacement for the flaky native
  //     HTML5 drag initiation (see the _mousePressActive field docs) ────────────

  /**
   * Starts tracking a left-button mouse press on a card. preventDefault() is
   * the key: it stops the browser's built-in drag/selection listeners from
   * arming on the `draggable` element — the native initiation was the 50 %
   * "nothing happens" bug (lost whenever the pointer started on the card art
   * <img draggable="false"> or while the previous click's pending selection /
   * double-click state was active). Buttons keep their clicks (no drag
   * starts from a button — the Play/Sell/Upgrade actions stay tap-able).
   * A press released under MOUSE_DRAG_THRESHOLD is a normal click (or the
   * first half of the dblclick-to-upgrade) — nothing is consumed.
   */
  private _onCardMouseDown(e: MouseEvent, source: DragSource, instanceId: string, index: number, element: HTMLElement, card?: { baseMinionId?: string; minionType?: string }): void {
    if (e.button !== 0) return; // left button only
    if ((e.target as HTMLElement | null)?.closest?.('button')) return; // let button clicks through
    if (!this._options.isDragEnabled) return; // phase-gated (same as the touch path)
    // A touch press may still be tracked (hybrid laptops) — don't clobber it.
    if (this._touchDragData) return;

    // Suppress the browser's built-in drag/selection on the draggable card.
    e.preventDefault();

    this._mouseStartX = e.clientX;
    this._mouseStartY = e.clientY;
    this._mouseIsDragging = false;
    this._mousePressActive = true;
    this._touchDragData = {
      source, instanceId, originalIndex: index,
      baseMinionId: card?.baseMinionId, minionType: card?.minionType,
    };
    this._dragSourceElement = element;
  }

  /** Document-level mousemove: starts the drag past the threshold, then moves the ghost + drop hints. */
  private _onGlobalMouseMove = (e: MouseEvent): void => {
    if (!this._mousePressActive || !this._touchDragData || !this._dragSourceElement) return;

    const dx = e.clientX - this._mouseStartX;
    const dy = e.clientY - this._mouseStartY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!this._mouseIsDragging && distance > MOUSE_DRAG_THRESHOLD) {
      this._mouseIsDragging = true;

      // Visual feedback — identical to the touch drag start.
      this._dragSourceElement.classList.add(DRAGGING_CLASS);
      this._dragSourceElement.style.opacity = '0.4';
      this._dragSourceElement.style.pointerEvents = 'none';
      this._createTouchGhost(this._dragSourceElement, e.clientX, e.clientY);
      if (this._touchDragData.source === 'battlefield') {
        const battlefield = this._dragSourceElement.parentElement;
        if (battlefield) {
          this._createPlaceholder(battlefield, this._dragSourceElement);
        }
      }
      this._showSellZoneBase();
      this._options.onDragStart?.(this._touchDragData, this._dragSourceElement);
    }

    if (this._mouseIsDragging) {
      if (this._touchGhost) {
        this._touchGhost.style.left = (e.clientX - this._touchGhost.offsetWidth / 2) + 'px';
        this._touchGhost.style.top = (e.clientY - this._touchGhost.offsetHeight / 2) + 'px';
      }
      this._updateTouchDropHighlight(e.clientX, e.clientY);
      this._updateSpellTargetGlow(e.clientX, e.clientY);
    }
  };

  /** Document-level mouseup: resolves the drop (or ends a plain click). */
  private _onGlobalMouseUp = (e: MouseEvent): void => {
    if (!this._mousePressActive) return;
    const wasDragging = this._mouseIsDragging;
    if (wasDragging) {
      // Remove ghost BEFORE elementFromPoint — it blocks hit-testing.
      this._removeTouchGhost();
      this._resolveDropAtRelease(e.clientX, e.clientY);
    }
    this._cleanupTouchState(); // clears the shared press slot + all artifacts
    this._mousePressActive = false;
    this._mouseIsDragging = false;
  };

  /**
   * The mouse gesture was interrupted (window blur, a context menu) —
   * cancel like a touchcancel: drop artifacts, report a failed drag only if
   * one was in progress.
   */
  private _onMouseCancel = (): void => {
    if (!this._mousePressActive) return;
    // Capture BEFORE cleanup (it nulls the shared slot) — mirrors
    // _onTouchCancel: only a drag that was in progress reports a failure.
    const dragData = this._touchDragData;
    const wasDragging = this._mouseIsDragging;
    this._removeTouchGhost();
    this._cleanupTouchState();
    this._mousePressActive = false;
    this._mouseIsDragging = false;
    if (dragData && wasDragging) {
      this._options.onDragEnd?.(dragData, false);
    }
  };

  /**
   * The touch sequence was interrupted by the browser/OS mid-gesture: the
   * native long-press hijack on a draggable card (disarmed by
   * CardInspectService — this is the backstop), a system gesture, an
   * incoming call, an edge swipe. Cleans up ALL touch-drag artifacts —
   * without this an interrupted mid-drag would leave the ghost, the
   * placeholder, the drop highlights and the 40 %-opacity source element on
   * screen. Mirrors _onTouchEnd's semantics: only a drag that was actually
   * in progress reports a (failed) drop. (touchcancel is not cancelable —
   * nothing to preventDefault.)
   */
  private _onTouchCancel(): void {
    const dragData = this._touchDragData;
    const wasDragging = this._touchIsDragging;
    this._cleanupTouchState();
    if (dragData && wasDragging) {
      this._options.onDragEnd?.(dragData, false);
    }
  }

  /**
   * True when a touch release lands on the given board — either directly on
   * it (element containment, as before) or anywhere in its WIDENED drop zone:
   * the board's vertical band at FULL screen width. The arena backdrop
   * (#battlefield-area) spans the whole match screen, so a release slightly
   * to the left or right of the card row — over the arena, not the
   * (narrower, centered) #battlefield container — is a board drop, not a
   * miss. (In jsdom all rects are zero, so the widened part is inert and
   * containment alone decides — existing tests keep their behavior.)
   */
  private _isOverBoard(container: HTMLElement | null, targetElement: Element | null, clientY: number): boolean {
    if (!container) return false;
    if (targetElement && (targetElement === container || container.contains(targetElement))) return true;
    const zone = this._getBoardDropZone(container);
    return !!zone && clientY >= zone.top && clientY <= zone.bottom;
  }

  /**
   * The board's drop zone as a vertical band { top, bottom }. The horizontal
   * extent is intentionally the WHOLE screen width — the same width as the
   * sell zone (the arena backdrop is the visual board), so callers only
   * test Y. The band spans the WHOLE arena (#battlefield-area) from its TOP
   * (the sell boundary: top of the played cards minus a few px — or the
   * container top on an empty board) down to the arena's LOWER EDGE: when
   * playing a card from the hand, the drop area is as wide as the sell
   * area — together the two full-width bands partition the screen (above
   * the sell line = sell, anywhere in the arena at/below it = play), so a
   * release in the arena — not just on the narrow centered card row — is a
   * board drop. Null when the board has no layout (jsdom / hidden) —
   * callers then fall back to plain element containment (releases over the
   * container itself always stay board drops, even in that few-px band,
   * so a reposition never accidentally sells).
   */
  private _getBoardDropZone(container: HTMLElement): { top: number; bottom: number } | null {
    if (typeof window === 'undefined') return null;
    const rect = container.getBoundingClientRect();
    if (!(rect.top > 0 && rect.bottom > rect.top)) return null;
    const sellBottom = this._getSellZoneBottom();
    // The arena (#battlefield-area) is the visual board — the band covers it
    // fully (its lower edge can sit below the narrow centered card row).
    // Without an arena (or without layout — jsdom) the container's own
    // bottom is the fallback.
    const arena = document.getElementById('battlefield-area') as HTMLElement | null;
    const arenaRect = arena ? arena.getBoundingClientRect() : null;
    const bottom = arenaRect && arenaRect.bottom > rect.bottom ? arenaRect.bottom : rect.bottom;
    return { top: Math.max(rect.top, sellBottom), bottom };
  }

  private _cleanupTouchState(): void {
    this._clearLongPressLockTimer();
    this._touchLongPressLocked = false;
    // A touch release/cancel ends the field-spell cast zone (task 178).
    this._spellZoneEl?.classList.remove('visible');
    if (this._dragSourceElement) {
      this._dragSourceElement.classList.remove(DRAGGING_CLASS);
      this._dragSourceElement.style.opacity = '';
      this._dragSourceElement.style.pointerEvents = '';
      this._dragSourceElement = null;
    }
    this._removeTouchGhost();
    this._removePlaceholder();
    this._removeHandDropPlaceholder();
    this._clearAllDropTargets();
    this._clearSpellTargetGlows();
    this._clearSellZoneHighlight();
    this._touchDragData = null;
    this._touchIsDragging = false;
    this._currentDrag = null;
  }

  private _createTouchGhost(sourceElement: HTMLElement, x: number, y: number): void {
    this._removeTouchGhost();

    const ghost = sourceElement.cloneNode(true) as HTMLElement;
    // The ghost is a pure visual that must follow the pointer 1:1 — strip
    // one-shot FX trigger classes so a deep clone never REPLAYS an
    // animation (fx-card-draw's 170px slide-in would offset the ghost
    // from the cursor while it plays — the drag-draw-animation bug).
    ghost.classList.remove('fx-card-draw');
    ghost.style.position = 'fixed';
    ghost.style.left = (x - sourceElement.offsetWidth / 2) + 'px';
    ghost.style.top = (y - sourceElement.offsetHeight / 2) + 'px';
    ghost.style.zIndex = '10000';
    ghost.style.pointerEvents = 'none';
    ghost.style.opacity = '0.85';
    ghost.style.transform = 'rotate(3deg) scale(1.05)';
    ghost.style.width = sourceElement.offsetWidth + 'px';
    ghost.style.height = sourceElement.offsetHeight + 'px';
    document.body.appendChild(ghost);
    this._touchGhost = ghost;
  }

  private _removeTouchGhost(): void {
    if (this._touchGhost) {
      this._touchGhost.remove();
      this._touchGhost = null;
    }
  }

  private _updateTouchDropHighlight(x: number, y: number): void {
    this._clearAllDropTargets();

    // Temporarily hide ghost so elementFromPoint sees through it
    const ghostWasVisible = this._touchGhost;
    if (ghostWasVisible) {
      ghostWasVisible.style.display = 'none';
    }

    const targetElement = document.elementFromPoint(x, y);

    // Restore ghost
    if (ghostWasVisible) {
      ghostWasVisible.style.display = '';
    }

    // Sell zone: visible for the WHOLE live drag (the user must see the sell
    // boundary), strongly highlighted while the finger is inside it. The
    // split COOP zone highlights the half under the finger.
    if (this._options.isDragEnabled) {
      const sellZoneBottom = this._getSellZoneBottom();
      const zone = this._getOrCreateSellZone();
      zone.style.height = `${sellZoneBottom}px`;
      zone.classList.add(SELL_ZONE_VISIBLE_CLASS);
      const inside = y < sellZoneBottom;
      zone.classList.toggle(SELL_ZONE_ACTIVE_CLASS, inside);
      if (this._options.giveZoneEnabled) {
        const give = this._isGiveSide(x);
        for (const half of zone.querySelectorAll<HTMLElement>('.sell-zone-half')) {
          half.classList.toggle(SELL_ZONE_ACTIVE_CLASS, inside && (half.classList.contains('give-half') === give));
        }
      }
    } else {
      this._clearSellZoneHighlight();
    }

    if (!targetElement) return;

    if (this._touchDragData?.source === 'hand') {
      const battlefield = document.getElementById('battlefield');
      // Over the board's drop zone (the whole arena band — as wide as the
      // sell zone) → play highlight (specific zone wins over sell). The
      // band test (not plain containment) also lights the board when the
      // finger is over the arena backdrop beside the card row.
      if (battlefield && this._isOverBoard(battlefield, targetElement, y)) {
        const fieldSpell = this._isFieldSpell(this._touchDragData);
        // FULL board: no play highlight (the release is rejected) — the
        // RED "No space" indicator still tracks the finger (task 175).
        battlefield.classList.toggle(DROP_TARGET_CLASS, !fieldSpell && !this._isBoardFull(battlefield));
        // FIELD-targeting spell: the whole board zone glows green for the
        // finger too (task 178 — same indicator as the mouse path).
        this._updateSpellBoardZone(battlefield, fieldSpell);
        // SPELLS cast on release — no insertion slot; a targeted spell
        // additionally outlines the minion under the finger (its target).
        if (this._touchDragData.minionType === SPELL_MINION_TYPE) {
          this._removeHandDropPlaceholder();
          for (const m of battlefield.querySelectorAll<HTMLElement>('.battlefield-minion')) {
            m.classList.remove(DROP_TARGET_CLASS);
          }
          const hovered = targetElement?.closest?.('.battlefield-minion') as HTMLElement | null;
          if (this._touchDragData.baseMinionId &&
              getSpellDefinition(this._touchDragData.baseMinionId)?.target === 'minion') {
            hovered?.classList.add(DROP_TARGET_CLASS);
          }
        } else {
          this._updateHandDropPlaceholder(battlefield, x);
        }
      }
    } else if (this._touchDragData?.source === 'battlefield') {
      const ownBattlefield = this._dragSourceElement?.parentElement;
      // Same arena-wide band as the release test in _onTouchEnd (see
      // _isOverBoard) — the reposition affordance follows the finger over
      // the whole arena, not just the narrow card row.
      const overOwnBoard = !!ownBattlefield && this._isOverBoard(ownBattlefield, targetElement, y);
      if (overOwnBoard && ownBattlefield) {
        // Over the own board (another minion, the placeholder, or a gap)
        // → reposition affordance: the whole board is a valid drop zone
        // (see _onTouchEnd), so keep the drop-slot placeholder under the
        // finger and outline the minion being hovered (if any).
        this._movePlaceholderForX(ownBattlefield, x);
        const droppedMinion = targetElement.closest('.battlefield-minion') as HTMLElement | null;
        if (droppedMinion && droppedMinion !== this._dragSourceElement) {
          droppedMinion.classList.add(DROP_TARGET_CLASS);
          const rect = droppedMinion.getBoundingClientRect();
          const isRightHalf = x - rect.left > rect.width / 2;
          droppedMinion.style.borderLeft = isRightHalf ? '' : '3px solid #f1c40f';
          droppedMinion.style.borderRight = !isRightHalf ? '3px solid #f1c40f' : '';
        }
      }
    }
  }

  // ─── Placeholder & Visual Helpers ────────────────────────────────────────

  private _setDragImage(event: DragEvent, element: HTMLElement): void {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.top = '-9999px';
    clone.style.width = element.offsetWidth + 'px';
    clone.style.height = element.offsetHeight + 'px';
    clone.style.opacity = '0.8';
    clone.style.transform = 'rotate(3deg)';
    clone.style.pointerEvents = 'none';
    document.body.appendChild(clone);

    const dt = event.dataTransfer as DataTransfer;
    if (dt.setDragImage) {
      dt.setDragImage(clone, element.offsetWidth / 2, element.offsetHeight / 2);
    }
    requestAnimationFrame(() => clone.remove());
  }

  private _createPlaceholder(container: HTMLElement, sourceElement: HTMLElement): void {
    this._removePlaceholder();

    const placeholder = document.createElement('div');
    placeholder.className = PLACEHOLDER_CLASS;
    // Size the slot to the source card's VISUAL (zoomed) size:
    // getBoundingClientRect carries the row's `zoom`, while offsetWidth
    // does NOT on Chromium — the old offsetWidth sizing left the slot at
    // base size ("always too small") on desktop boards that scale up.
    const srcRect = sourceElement.getBoundingClientRect();
    placeholder.style.width = (srcRect.width > 0 ? srcRect.width : 100) + 'px';
    placeholder.style.height = (srcRect.height > 0 ? srcRect.height : 130) + 'px';
    placeholder.style.border = '2px dashed #f1c40f';
    placeholder.style.borderRadius = '8px';
    placeholder.style.background = 'rgba(241, 196, 15, 0.1)';
    placeholder.style.flexShrink = '0';

    placeholder.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const dt = e.dataTransfer as DataTransfer;
      dt.dropEffect = 'move';
    });

    placeholder.addEventListener('drop', (e: DragEvent) => {
      const dragData = this._resolveDragData(e);
      // Only claim battlefield (reposition) drags — anything else bubbles
      // up to the battlefield container's drop handler.
      if (!dragData || dragData.source !== 'battlefield') return;
      e.preventDefault();
      e.stopPropagation();
      (e as unknown as Record<string, unknown>)[DROP_HANDLED_MARKER] = true;

      let insertBefore = 0;
      for (const child of container.children) {
        if (child === this._placeholder) break;
        if (!child.classList.contains(PLACEHOLDER_CLASS)) insertBefore++;
      }
      this._options.onReposition(dragData.instanceId, insertBefore);
      this._currentDrag = null;
    });

    container.insertBefore(placeholder, sourceElement);
    this._placeholder = placeholder;
  }

  private _removePlaceholder(): void {
    if (this._placeholder) {
      this._placeholder.remove();
      this._placeholder = null;
    }
  }

  /**
   * Moves the reposition placeholder to the slot indicated by the cursor's
   * X coordinate (the same geometry the container's drop handler uses to
   * compute the insertion index). Used by the container-level dragover so
   * the visible drop slot follows the pointer in the gaps between cards —
   * regions no minion-level dragover covers.
   */
  private _movePlaceholderForX(container: HTMLElement, clientX: number): void {
    if (!this._placeholder) return;
    const insertIndex = this._calculateBattlefieldDropPosition(container, clientX);
    const children = Array.from(container.children).filter(
      (c) => !c.classList.contains(PLACEHOLDER_CLASS)
    );
    if (insertIndex < children.length) {
      container.insertBefore(this._placeholder, children[insertIndex]);
    } else {
      container.appendChild(this._placeholder);
    }
  }

  /** The player's board capacity (option, default GameConfig.maxBoardSize). */
  private _boardLimit(): number {
    return this._options.boardSizeLimit ?? GameConfig.maxBoardSize;
  }

  /** The field-spell cast-zone overlay element (lazy; may be absent in tests). */
  private _getSpellZoneEl(): HTMLElement | null {
    if (!this._spellZoneEl) {
      this._spellZoneEl = document.getElementById('spell-board-zone');
    }
    return this._spellZoneEl;
  }

  /**
   * Is this drag data a FIELD-targeting spell (Healing Rain / Acid Rain / …)?
   * Those cast on the WHOLE board (no minion target) — the full-area green
   * zone indicator applies to them (task 178); minion-targeting spells
   * keep the per-minion outline only.
   */
  private _isFieldSpell(drag: DragData | null): boolean {
    return !!drag && drag.minionType === SPELL_MINION_TYPE &&
      !!drag.baseMinionId && getSpellDefinition(drag.baseMinionId)?.target === 'battlefield';
  }

  /**
   * Show/hide the full-area green cast zone for field-targeting spells
   * (task 178): the overlay covers the EXACT board drop zone (full width,
   * sell line → arena bottom — `_getBoardDropZone`) on every dragover, so
   * the player sees the whole area where the spell can be released.
   */
  private _updateSpellBoardZone(container: HTMLElement, show: boolean): void {
    const el = this._getSpellZoneEl();
    if (!el) return;
    if (!show) {
      el.classList.remove('visible');
      return;
    }
    const zone = this._getBoardDropZone(container);
    if (!zone) {
      // No layout (e.g. jsdom) — the indicator can't be placed.
      el.classList.remove('visible');
      return;
    }
    el.style.top = `${zone.top}px`;
    el.style.height = `${Math.max(0, zone.bottom - zone.top)}px`;
    el.classList.add('visible');
  }

  /** Is the given battlefield row FULL (no slot left for a new card)? */
  private _isBoardFull(battlefieldContainer: HTMLElement): boolean {
    return battlefieldContainer.querySelectorAll('.battlefield-minion').length >= this._boardLimit();
  }

  private _updateHandDropPlaceholder(battlefieldContainer: HTMLElement, clientX: number): void {
    // FULL BOARD (playtest, task 175): no green "Drop here" — a RED
    // "No space" indicator in the same slot geometry (the drop itself is
    // rejected: dropEffect 'none' + the drop handlers' capacity check).
    const full = this._isBoardFull(battlefieldContainer);
    const accent = full ? '#e74c3c' : '#2ecc71';
    if (!this._handDropPlaceholder) {
      this._handDropPlaceholder = document.createElement('div');
      this._handDropPlaceholder.className = 'hand-drop-placeholder';
      // Size the slot to the ZOOMED board cards (getBoundingClientRect
      // carries the row zoom; offsetWidth doesn't on Chromium) so it
      // matches the card the drop will create. While the board is empty
      // its scale is 1 — the base 100×130 board card is the reference.
      const ref = battlefieldContainer.querySelector<HTMLElement>('.battlefield-minion');
      const refRect = ref?.getBoundingClientRect();
      this._handDropPlaceholder.style.width =
        (refRect && refRect.width > 0 ? refRect.width : 100) + 'px';
      this._handDropPlaceholder.style.height =
        (refRect && refRect.height > 0 ? refRect.height : 130) + 'px';
      this._handDropPlaceholder.style.borderRadius = '8px';
      this._handDropPlaceholder.style.flexShrink = '0';
      this._handDropPlaceholder.style.display = 'flex';
      this._handDropPlaceholder.style.alignItems = 'center';
      this._handDropPlaceholder.style.justifyContent = 'center';
      this._handDropPlaceholder.style.fontSize = '11px';
      this._handDropPlaceholder.style.fontWeight = '600';
      battlefieldContainer.appendChild(this._handDropPlaceholder);
    }
    // Restyled on EVERY call: the board can fill up (or not) mid-drag, so
    // green ↔ red must track the live minion count, not creation time.
    this._handDropPlaceholder.style.border = `2px dashed ${accent}`;
    this._handDropPlaceholder.style.background = full
      ? 'rgba(231, 76, 60, 0.14)'
      : 'rgba(46, 204, 113, 0.12)';
    this._handDropPlaceholder.style.color = accent;
    this._handDropPlaceholder.textContent = full ? 'No space' : 'Drop here';

    const minionEls = Array.from(battlefieldContainer.querySelectorAll('.battlefield-minion'));
    let insertBeforeIndex = minionEls.length;

    for (let i = 0; i < minionEls.length; i++) {
      const rect = minionEls[i].getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      if (clientX < midX) {
        insertBeforeIndex = i;
        break;
      }
    }

    const allChildren = Array.from(battlefieldContainer.children);
    const placeholder = this._handDropPlaceholder;
    const nonPlaceholderChildren = allChildren.filter(c => c !== placeholder);
    if (insertBeforeIndex < nonPlaceholderChildren.length) {
      battlefieldContainer.insertBefore(placeholder, nonPlaceholderChildren[insertBeforeIndex]);
    } else {
      battlefieldContainer.appendChild(placeholder);
    }
  }

  private _removeHandDropPlaceholder(): void {
    if (this._handDropPlaceholder) {
      this._handDropPlaceholder.remove();
      this._handDropPlaceholder = null;
    }
  }

  private _clearDropTargets(container: HTMLElement): void {
    const targets = container.querySelectorAll(`.${DROP_TARGET_CLASS}`);
    for (const target of targets) {
      target.classList.remove(DROP_TARGET_CLASS);
      (target as HTMLElement).style.borderLeft = '';
      (target as HTMLElement).style.borderRight = '';
    }
  }

  private _clearAllDropTargets(): void {
    const dropTargets = document.querySelectorAll(`.${DROP_TARGET_CLASS}`);
    for (const target of dropTargets) {
      target.classList.remove(DROP_TARGET_CLASS);
      (target as HTMLElement).style.borderLeft = '';
      (target as HTMLElement).style.borderRight = '';
    }
    const handPlaceholders = document.querySelectorAll('.hand-drop-placeholder');
    for (const ph of handPlaceholders) {
      ph.remove();
    }
    // The field-spell cast zone is a drag artifact too — every cleanup
    // (dragend, reset, dispose) goes through here.
    this._updateSpellBoardZone(document.getElementById('battlefield') ?? document.body, false);
  }

  /**
   * The battlefield minion under the given viewport point (the target for a
   * targeted spell cast released over the arena backdrop — the document-level
   * drop handler has no e.target inside the card row to read from). Null
   * when nothing (or a non-minion) is under the point.
   */
  private _spellTargetAt(clientX: number, clientY: number): string | null {
    const el = document.elementFromPoint(clientX, clientY);
    const minionEl = el?.closest?.('.battlefield-minion') as HTMLElement | null;
    return minionEl?.dataset.instanceId ?? null;
  }

  /**
   * Resolves the drag data for the ACTIVE Project_X drag.
   *
   * Prefers the service's own drag-start record (`_currentDrag`) over
   * reading the dataTransfer: in real browsers the dataTransfer data is
   * PROTECTED during dragover (getData() returns '' — only types is
   * visible), so a dragover handler that parses the dataTransfer can never
   * see the payload. (jsdom and the test mocks don't enforce this protection,
   * which is why the old code looked fine in tests but showed no drop
   * feedback in the browser.) The dataTransfer parse remains as a fallback
   * for drop events and for synthetic events in tests.
   */
  private _resolveDragData(event: DragEvent): DragData | null {
    if (this._currentDrag) return this._currentDrag;
    return this._parseDragData(event);
  }

  private _parseDragData(event: DragEvent): DragData | null {
    const dt = event.dataTransfer as DataTransfer;
    if (!dt) return null;
    const raw = dt.getData(DRAG_DATA_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as DragData; } catch { return null; }
  }

  /**
   * Calculates the target battlefield position (0-indexed) for a card dropped
   * at the given X coordinate. Uses the drop position relative to existing
   * minion elements to determine the insertion index.
   */
  private _calculateBattlefieldDropPosition(
    battlefieldContainer: HTMLElement,
    clientX: number
  ): number {
    const existingMinions = battlefieldContainer.querySelectorAll('.battlefield-minion');
    if (existingMinions.length === 0) {
      return 0; // Empty battlefield, place at position 0
    }

    const containerRect = battlefieldContainer.getBoundingClientRect();
    const relativeX = clientX - containerRect.left;

    // Check if dropped on an existing minion
    for (let i = 0; i < existingMinions.length; i++) {
      const minionEl = existingMinions[i] as HTMLElement;
      const minionRect = minionEl.getBoundingClientRect();
      const minionLeft = minionRect.left - containerRect.left;
      const minionRight = minionLeft + minionRect.width;

      if (relativeX >= minionLeft && relativeX <= minionRight) {
        // Dropped on this minion — left half = before, right half = after
        const isRightHalf = relativeX - minionLeft > minionRect.width / 2;
        return isRightHalf ? i + 1 : i;
      }
    }

    // Dropped between minions or outside — find the closest slot
    // If dropped to the left of the first minion, position 0
    const firstMinionRect = (existingMinions[0] as HTMLElement).getBoundingClientRect();
    const firstMinionLeft = firstMinionRect.left - containerRect.left;
    if (relativeX < firstMinionLeft) {
      return 0;
    }

    // If dropped to the right of the last minion, position at end
    const lastMinionRect = (existingMinions[existingMinions.length - 1] as HTMLElement).getBoundingClientRect();
    const lastMinionRight = (lastMinionRect.left - containerRect.left) + lastMinionRect.width;
    if (relativeX > lastMinionRight) {
      return existingMinions.length;
    }

    // Find the gap where the drop occurred
    for (let i = 0; i < existingMinions.length; i++) {
      const currentMinionRect = (existingMinions[i] as HTMLElement).getBoundingClientRect();
      const currentLeft = currentMinionRect.left - containerRect.left;
      if (relativeX < currentLeft) {
        return i;
      }
    }

    // Fallback: append at end
    return existingMinions.length;
  }

  // ─── Static Animation Methods ────────────────────────────────────────────

  /**
   * The impact moment (task: impact timing): fires when the attacker
   * reaches the defender — the END of the lunge phase, i.e. the FURTHEST
   * point from the starting position (the contact). main.ts schedules the
   * impact FX + sound here, NOT on the stream's damage event (which would
   * land after the whole animation).
   */
  static animateAttack(
    attackerElement: HTMLElement,
    defenderElement: HTMLElement,
    duration: number = 600,
    onImpact?: () => void,
  ): Promise<void> {
    return new Promise((resolve) => {
      if (!attackerElement || !defenderElement) { resolve(); return; }

      const attackerRect = attackerElement.getBoundingClientRect();
      const defenderRect = defenderElement.getBoundingClientRect();
      const deltaX = defenderRect.left + defenderRect.width / 2 - (attackerRect.left + attackerRect.width / 2);
      const deltaY = defenderRect.top + defenderRect.height / 2 - (attackerRect.top + attackerRect.height / 2);
      const moveX = deltaX * 0.6;
      const moveY = deltaY * 0.6;

      const attackDuration = duration * 0.3;
      const pauseDuration = duration * 0.1;
      const returnDuration = duration * 0.3;

      attackerElement.style.transition = `transform ${attackDuration}ms ease-in`;
      attackerElement.style.transform = `translate(${moveX}px, ${moveY}px) scale(1.1)`;
      attackerElement.style.zIndex = '1000';
      attackerElement.style.filter = 'brightness(1.3)';

      // THE IMPACT: at the end of the lunge (max displacement = contact).
      if (onImpact) {
        setTimeout(onImpact, attackDuration);
      }

      setTimeout(() => {
        attackerElement.style.transition = `transform ${returnDuration}ms ease-out, filter ${returnDuration}ms ease-out`;
        attackerElement.style.transform = 'translate(0, 0) scale(1)';
        attackerElement.style.filter = 'brightness(1)';

        setTimeout(() => {
          attackerElement.style.transition = '';
          attackerElement.style.transform = '';
          attackerElement.style.zIndex = '';
          attackerElement.style.filter = '';
          resolve();
        }, returnDuration);
      }, attackDuration + pauseDuration);
    });
  }

  static animateSimultaneousDamage(
    attackerElement: HTMLElement,
    defenderElement: HTMLElement,
    damageToAttacker: number,
    damageToDefender: number,
  ): void {
    if (attackerElement && damageToAttacker > 0) this._flashDamage(attackerElement, damageToAttacker);
    if (defenderElement && damageToDefender > 0) this._flashDamage(defenderElement, damageToDefender);
  }

  private static _flashDamage(element: HTMLElement, damage: number): void {
    const flash = document.createElement('div');
    flash.className = 'damage-flash';
    flash.textContent = `-${damage}`;
    // Scale the flash with the magnitude of the hit (34px baseline for small
    // hits, ~64px cap for huge ones) so big damage reads as big damage.
    flash.style.fontSize = `${Math.min(34 + Math.max(0, damage - 5) * 1.6, 64)}px`;
    element.appendChild(flash);
    element.style.animation = 'shake 0.3s ease-in-out';
    setTimeout(() => {
      element.style.animation = '';
      flash.remove();
    }, 600);
  }
}
